import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  useGetRdShares,
  useGetProjects,
  useGetClaimExpenditure,
  useDeriveRdShares,
  useAdoptRdShare,
  getGetRdSharesQueryKey,
  getGetProjectsQueryKey,
  getGetClaimExpenditureQueryKey,
  type ProjectRdShare,
} from '@edison/api-client-react';
import { Card } from '@/components/ui/card.js';
import { Button } from '@/components/ui/button.js';
import { Bar } from '@/components/ui/readiness-row.js';
import { SkeletonCard, SkeletonMetricTile } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { useAuthStore } from '@/store/auth-store.js';
import { ShareRow, type ShareRowData, type Triple, type AdoptRequest } from './share-row.js';
import { AdoptShareDialog } from './adopt-share-dialog.js';
import { CommitDrilldownPanel } from './commit-drilldown-panel.js';

const MANAGE_ROLES = new Set(['tenant_admin', 'r_and_d_manager']);

// The exact copy the brief specifies for the "no classified evidence yet" empty state — reused
// for both the tab's own empty state and (independently) the commit drill-down panel's.
export const NO_COMMITS_EMPTY_STATE = 'No classified commits yet — run the classification batch.';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

function pct(v: string | null | undefined): number {
  return v != null ? parseFloat(v) : 0;
}

// Which triple represents the "current" view of a project for the combined header: the frozen
// adopted figure once officially adopted; the latest derived figure otherwise (covers both
// never-adopted and stale rows, where the adopted figure is known out of date). Never a blend of
// the two — that would be a fabricated number no reviewer actually approved.
function currentTriple(share: ProjectRdShare): Triple {
  if (share.status === 'adopted') {
    return { corePct: pct(share.adoptedCorePct), supportingPct: pct(share.adoptedSupportingPct), outPct: pct(share.adoptedOutPct) };
  }
  return { corePct: pct(share.derivedCorePct), supportingPct: pct(share.derivedSupportingPct), outPct: pct(share.derivedOutPct) };
}

interface ApiErrorShape {
  response?: { data?: { error?: { code?: string; message?: string; details?: unknown } } };
}

function extractErrorMessage(err: unknown, fallback: string): string {
  return (err as ApiErrorShape)?.response?.data?.error?.message ?? fallback;
}

interface Props {
  claimId: string;
  incomeYear: string;
}

/**
 * R&D share adoption tab (Stage 1 apportionment evidence store, Task 12): the human adoption
 * surface over Task 8's derive/adopt/commits endpoints. Every project with an adopted share must
 * exist before the claim's apportionment labour figure can compute (services/apportionment-labour.ts
 * ApportionmentNotReadyError, surfaced here as the claim expenditure endpoint's 409 SHARES_NOT_ADOPTED).
 */
export function RdSharesTab({ claimId, incomeYear }: Props) {
  const { user } = useAuthStore();
  const canManage = user?.role ? MANAGE_ROLES.has(user.role) : false;
  const queryClient = useQueryClient();

  const sharesQuery = useGetRdShares({ incomeYear }, { query: { queryKey: getGetRdSharesQueryKey({ incomeYear }) } });
  const projectsQuery = useGetProjects({ incomeYear }, { query: { queryKey: getGetProjectsQueryKey({ incomeYear }) } });
  const expenditureQuery = useGetClaimExpenditure(claimId);

  const deriveMutation = useDeriveRdShares();
  const adoptMutation = useAdoptRdShare();

  const [deriveMessage, setDeriveMessage] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<{ projectId: string; projectName: string } | null>(null);
  const [adopting, setAdopting] = useState<AdoptRequest | null>(null);
  const [adoptError, setAdoptError] = useState<string | null>(null);

  const shares = sharesQuery.data?.data?.items ?? [];
  const projects = projectsQuery.data?.data?.items ?? [];
  const projectById = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects]);

  const rows: ShareRowData[] = useMemo(() => {
    const withShares: ShareRowData[] = shares.map((s) => {
      const project = projectById.get(s.projectId);
      return {
        projectId: s.projectId,
        projectName: project?.name ?? s.projectId.slice(0, 8),
        tfaLocked: project?.tfaLocked ?? null,
        isNoRd: project?.status === 'no_rd',
        share: s,
      };
    });
    // Non-R&D projects are categorically 100% out — they get a preset row (no adoption needed)
    // even when they have no derived share, since derivation only ever runs over classified
    // commits and a no_rd project's status already settles the question.
    const sharedProjectIds = new Set(shares.map((s) => s.projectId));
    const noRdOnly: ShareRowData[] = projects
      .filter((p) => p.status === 'no_rd' && !sharedProjectIds.has(p.id))
      .map((p) => ({ projectId: p.id, projectName: p.name, tfaLocked: p.tfaLocked ?? null, isNoRd: true, share: undefined }));
    return [...withShares, ...noRdOnly].sort((a, b) => a.projectName.localeCompare(b.projectName));
  }, [shares, projects, projectById]);

  // Combined core/supporting/out totals, commit-count-weighted across every project with FY
  // effort — using each project's adopted figure once adopted, else its latest derived figure.
  const combined = useMemo(() => {
    let coreSum = 0;
    let supportingSum = 0;
    let outSum = 0;
    let totalCommits = 0;
    for (const s of shares) {
      const commitCount = s.derivedCommitCount ?? 0;
      if (commitCount <= 0) continue;
      const t = currentTriple(s);
      coreSum += commitCount * (t.corePct / 100);
      supportingSum += commitCount * (t.supportingPct / 100);
      outSum += commitCount * (t.outPct / 100);
      totalCommits += commitCount;
    }
    if (totalCommits === 0) return null;
    return {
      core: (coreSum / totalCommits) * 100,
      supporting: (supportingSum / totalCommits) * 100,
      out: (outSum / totalCommits) * 100,
    };
  }, [shares]);

  function handleDerive() {
    setDeriveMessage(null);
    deriveMutation.mutate(
      { data: { incomeYear } },
      {
        onSuccess: (res) => {
          const n = res.data?.items?.length ?? 0;
          setDeriveMessage(
            n > 0 ? `Derived ${n} project${n === 1 ? '' : 's'}.` : 'No classified commits found for this income year yet.',
          );
          void queryClient.invalidateQueries({ queryKey: getGetRdSharesQueryKey({ incomeYear }) });
        },
        onError: (err) => setDeriveMessage(extractErrorMessage(err, 'Failed to run derivation.')),
      },
    );
  }

  function handleConfirmAdopt(note: string | null) {
    if (!adopting) return;
    setAdoptError(null);
    adoptMutation.mutate(
      {
        projectId: adopting.projectId,
        data: {
          incomeYear: adopting.incomeYear,
          corePct: adopting.pending.corePct,
          supportingPct: adopting.pending.supportingPct,
          outPct: adopting.pending.outPct,
          note,
        },
      },
      {
        onSuccess: () => {
          setAdopting(null);
          void queryClient.invalidateQueries({ queryKey: getGetRdSharesQueryKey({ incomeYear }) });
          // The labour figure (expenditure endpoint) gates on every effort-bearing project being
          // adopted — re-fetch it so the header's withheld/withheld-no-more state stays in sync.
          void queryClient.invalidateQueries({ queryKey: getGetClaimExpenditureQueryKey(claimId) });
        },
        onError: (err) => setAdoptError(extractErrorMessage(err, 'Failed to adopt R&D share.')),
      },
    );
  }

  if (sharesQuery.isLoading || projectsQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => <SkeletonMetricTile key={i} />)}
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (sharesQuery.error || projectsQuery.error) {
    return (
      <InlineError
        message="Failed to load R&D shares."
        onRetry={() => { void sharesQuery.refetch(); void projectsQuery.refetch(); }}
      />
    );
  }

  const expError = expenditureQuery.error as ApiErrorShape | null;
  const sharesNotAdopted = expError?.response?.data?.error?.code === 'SHARES_NOT_ADOPTED';
  const awaitingProjectIds: string[] =
    sharesNotAdopted && Array.isArray(expError?.response?.data?.error?.details)
      ? (expError!.response!.data!.error!.details as string[])
      : [];

  const expenditure = expenditureQuery.data?.data as
    | { labour?: { totalRdExpenditure?: number; totalRdHours?: number } }
    | undefined;

  return (
    <div className="space-y-6">
      <Card variant="static" className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-medium">R&amp;D share rollup — {incomeYear}</h2>
          {canManage && (
            <div className="flex items-center gap-2">
              {deriveMessage && <span className="text-xs text-muted-foreground">{deriveMessage}</span>}
              <Button variant="secondary" size="sm" disabled={deriveMutation.isPending} onClick={handleDerive}>
                <RefreshCw className={`h-3.5 w-3.5 ${deriveMutation.isPending ? 'animate-spin' : ''}`} aria-hidden="true" />
                {deriveMutation.isPending ? 'Deriving…' : 'Run derivation'}
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="grid grid-cols-1 gap-3 md:col-span-3 md:grid-cols-3">
            {combined ? (
              <>
                <Bar label="Core" value={`${Math.round(combined.core * 10) / 10}%`} pct={combined.core} tone="indigo" />
                <Bar label="Supporting" value={`${Math.round(combined.supporting * 10) / 10}%`} pct={combined.supporting} tone="indigo" />
                <Bar label="Out" value={`${Math.round(combined.out * 10) / 10}%`} pct={combined.out} tone="indigo" />
              </>
            ) : (
              <p className="col-span-3 text-sm text-muted-foreground">{NO_COMMITS_EMPTY_STATE}</p>
            )}
          </div>

          <div>
            <p className="text-xs text-muted-foreground">Apportionment labour</p>
            {expenditureQuery.isLoading ? (
              <div className="h-8 w-24 rounded skeleton" />
            ) : sharesNotAdopted ? (
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Withheld — shares not adopted</p>
            ) : expenditureQuery.error ? (
              <p className="text-sm text-destructive">Failed to load</p>
            ) : (
              <p className="text-2xl font-semibold font-mono tabular-nums">
                {formatCurrency(expenditure?.labour?.totalRdExpenditure ?? 0)}
              </p>
            )}
          </div>
        </div>

        {sharesNotAdopted && awaitingProjectIds.length > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
            <AlertTriangle className="inline h-3 w-3 mr-1" aria-hidden="true" />
            R&amp;D shares not yet adopted for: {awaitingProjectIds.map((id) => projectById.get(id)?.name ?? id.slice(0, 8)).join(', ')}.{' '}
            Adopt each project&apos;s share below before this claim&apos;s labour figure can compute.
          </div>
        )}
      </Card>

      {rows.length === 0 && (
        <p className="py-12 text-center text-sm text-muted-foreground">{NO_COMMITS_EMPTY_STATE}</p>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          {rows.map((row) => (
            <ShareRow
              key={row.projectId}
              row={row}
              canManage={canManage}
              onOpenDrilldown={(projectId, projectName) => setDrilldown({ projectId, projectName })}
              onRequestAdopt={(request) => { setAdoptError(null); setAdopting(request); }}
            />
          ))}
        </div>
      )}

      <CommitDrilldownPanel
        open={drilldown !== null}
        onClose={() => setDrilldown(null)}
        projectId={drilldown?.projectId ?? null}
        projectName={drilldown?.projectName ?? ''}
        incomeYear={incomeYear}
      />

      <AdoptShareDialog
        open={adopting !== null}
        projectName={adopting?.projectName ?? ''}
        pending={adopting?.pending ?? null}
        derived={adopting?.derived ?? null}
        isSubmitting={adoptMutation.isPending}
        errorMessage={adoptError}
        onConfirm={handleConfirmAdopt}
        onCancel={() => { setAdopting(null); setAdoptError(null); }}
      />
    </div>
  );
}
